import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight, Linkedin, Twitter, Facebook, Instagram, Youtube } from 'lucide-react';

const footerLinks = {
  services: [
    { name: 'Policy Innovation', path: '/services#policy' },
    { name: 'Health Security', path: '/services#security' },
    { name: 'Health Technology', path: '/services#technology' },
    { name: 'Health Financing', path: '/services#financing' },
    { name: 'Leadership Development', path: '/services#leadership' },
    { name: 'Education R&D', path: '/education' },
    { name: 'IVA Services', path: '/iva' },
  ],
  company: [
    { name: 'About HSL', path: '/about' },
    { name: 'Methodology', path: '/methodology' },
    { name: 'Technical Pillars', path: '/technical-pillars' },
    { name: 'Resources', path: '/resources' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
    { name: 'Staff Portal', path: '/staff' },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-main py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4" data-testid="footer-logo">
              <div className="w-10 h-10 bg-teal-500 flex items-center justify-center">
                <span className="font-heading font-bold text-white text-lg">H</span>
              </div>
              <div>
                <span className="font-heading text-white text-xl font-semibold tracking-tight block leading-tight">
                  HSL
                </span>
                <span className="text-[10px] text-slate-400 tracking-wide uppercase">
                  Health & Social Development Consulting
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Health Strategy and Leadership. Transitioning global health and social development from default to design.
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com/company/haborlimited"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-teal-400 hover:border-teal-400 transition-colors"
                data-testid="footer-linkedin"
                aria-label="HSL LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://twitter.com/HSL_Limited"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-teal-400 hover:border-teal-400 transition-colors"
                data-testid="footer-twitter"
                aria-label="HSL Twitter/X"
              >
                <Twitter size={18} />
              </a>
              <a
                href="https://facebook.com/HSLLimited"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-teal-400 hover:border-teal-400 transition-colors"
                data-testid="footer-facebook"
                aria-label="HSL Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://instagram.com/hsl_limited"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-teal-400 hover:border-teal-400 transition-colors"
                data-testid="footer-instagram"
                aria-label="HSL Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://youtube.com/@HSLLimited"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-teal-400 hover:border-teal-400 transition-colors"
                data-testid="footer-youtube"
                aria-label="HSL YouTube"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal-400 text-sm transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight 
                      size={12} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity" 
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal-400 text-sm transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight 
                      size={12} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity" 
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-teal-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  No 4, Natasha Apoti Street,<br />
                  Kado District, Abuja, Nigeria
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-teal-400 flex-shrink-0" />
                <a 
                  href="tel:+2349163581584" 
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                  data-testid="footer-phone"
                >
                  +234 916 358 1584
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-teal-400 flex-shrink-0" />
                <a 
                  href="mailto:info@hsl.com" 
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                  data-testid="footer-email"
                >
                  info@hsl.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-main py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">
              © {currentYear} Health Strategy and Leadership Limited. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link 
                to="/privacy" 
                className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
